import { useAccountState } from "@/composables/useAccountState";
import { useUserStore } from "@/store/user";
import { getProviderType, type Account } from "@aircap/common/account";
import { accountMemberManagerRoles, supervisorRoles } from "@aircap/common/auth";
import type { Ref } from "vue";
import { computed } from "vue";
import type { MessageKey } from "./useI18n";

// Single definition of an account's navigation, rendered by the side menu. The active tab is the
// route itself, so the menu renders `:to` links from this list.
export const useAccountTabs = (account: Ref<(Account & { readonly id: string }) | undefined>) => {
  const user = useUserStore();
  const { accountState, accountStateUi } = useAccountState(account);

  const treezorOpening = computed(
    () =>
      account.value?.accountType === "treezor" &&
      (accountState.value?.status === "pending" || accountState.value?.status === "refused"),
  );
  const treezorKycReadyForAdminReview = computed(
    () => accountState.value?.status === "kycReadyForAdminReview",
  );
  const treezorOnboarding = computed(() => {
    if (!treezorOpening.value) return false;
    const kycStatus = account.value?.treezorCompany?.kyc?.kycStatus;
    return kycStatus !== "PENDING" && kycStatus !== "VALIDATED";
  });

  const pendingMember = computed(
    () => !!user.uid && account.value?.users?.[user.uid]?.role === "pendingAccountHolder",
  );

  const tabs = computed(() => {
    if (!account.value) return [];
    const holderOnboarding = user.isKind("user") && treezorOnboarding.value;
    const proAccount = getProviderType(account.value.accountType) === "proAccount";
    const openBanking = getProviderType(account.value.accountType) === "openBanking";
    const tabs: {
      name: string;
      title: MessageKey;
      icon: string;
      badge: number;
      visible: boolean;
    }[] = [
      {
        name: "MemberOnboarding",
        title: "sidemenu.accountOpening",
        icon: "mdi-account-check-outline",
        badge: 0,
        visible: pendingMember.value,
      },
      {
        name: "Opening",
        title: holderOnboarding ? "sidemenu.accountOpening" : "account.tabs.opening",
        icon: "mdi-file-document-edit-outline",
        badge: 0,
        visible:
          holderOnboarding ||
          ((treezorOpening.value || treezorKycReadyForAdminReview.value) && user.isKind("admin")),
      },
      {
        name: "Dashboard",
        title: "account.tabs.dashboard",
        icon: "mdi-view-dashboard-outline",
        badge: 0,
        visible: true,
      },
      {
        name: "Transactions",
        title: "account.tabs.transactions",
        icon: "mdi-swap-horizontal",
        badge: 0,
        visible: true,
      },
      {
        name: "TransferRequests",
        title: "account.tabs.transfersToValidate",
        icon: "mdi-bank-transfer",
        badge: account.value.pendingTransfersCount ?? 0,
        visible: proAccount,
      },
      {
        name: "Beneficiaries",
        title: "account.tabs.beneficiaries",
        icon: "mdi-account-multiple-outline",
        badge: 0,
        visible: proAccount,
      },
      {
        name: "Notifications",
        title: "account.tabs.notifications",
        icon: "mdi-bell-outline",
        badge: 0,
        visible: user.hasRole(supervisorRoles, { account: account.value }) && openBanking,
      },
      {
        name: "Budget",
        title: "account.tabs.budget",
        icon: "mdi-chart-pie",
        badge: 0,
        visible: true,
      },
      {
        name: "Commercialisation",
        title: "account.tabs.commercialisation",
        icon: "mdi-storefront-outline",
        badge: 0,
        visible: true,
      },
      {
        name: "Logs",
        title: "account.tabs.logs",
        icon: "mdi-text-box-outline",
        badge: 0,
        visible: user.isKind("admin"),
      },
      {
        name: "Members",
        title: "account.tabs.members",
        icon: "mdi-account-group-outline",
        badge: 0,
        visible: user.hasRole(accountMemberManagerRoles, { account: account.value }),
      },
      {
        name: "Settings",
        title: "account.tabs.settings",
        icon: "mdi-cog-outline",
        badge: 0,
        visible: user.hasRole(supervisorRoles, { account: account.value }),
      },
    ];
    // A member or holder still onboarding only gets their onboarding form
    const onlyTab = pendingMember.value ? "MemberOnboarding" : holderOnboarding ? "Opening" : null;
    return tabs.filter((tab) => tab.visible && (!onlyTab || tab.name === onlyTab));
  });

  return {
    accountState,
    accountStateUi,
    treezorOpening,
    treezorKycReadyForAdminReview,
    treezorOnboarding,
    pendingMember,
    tabs,
  };
};
